// AidenProfileBanner.jsx — the Morning Mode "Aiden profile" banner.
//
// Prototype (docs/pronto-prototype-spec.md · Morning §Recipe readout): a tonal
// banner (rgba(63,74,99,0.08), radius 14) with a dial-icon tile + "Aiden profile"
// eyebrow + a "Change ›" affordance (pick an existing saved profile), the profile
// name (editable when the recipe is new/edited), and a Send/Save button shown only
// when the brew needs pushing to the machine.
//
// Gating (spec):
//   needsSend = branch !== "repeat" OR temp was changed.
//   Repeat with no temp edit → banner shows the existing profile, NO send button
//   (it just selects the profile already on the Aiden).
//   sendLabel = "Save as a new Aiden profile" (new_bag / blend — brand-new profile)
//             = "Update this Aiden profile"    (tweak / temp-edit — mutate existing)
//
// Dose & grind are per-brew and never touch the Aiden; only TEMP updates the
// profile — so the profile we push is built from the brew's aidenProfile with the
// user's name. Wired to the aiden.js seam (pushProfileToAiden / listAidenProfiles)
// and coffee_favorites (store.favorites / store.saveFavorite) for the picker + a
// recallable saved copy. Degrades gracefully: when the proxy is unavailable the
// push falls back to "stub" mode and we surface the exact values to enter by hand.

import { useMemo, useState } from "react";
import { Eyebrow } from "./pronto";
import { pushProfileToAiden, listAidenProfiles, aidenProxyEnabled, formatProfileAsText } from "../lib/aiden";

// A tuned dial (SF-symbol stand-in), matching the prototype's inline SVG.
function DialIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" />
      <line x1="12" y1="12" x2="12" y2="5.5" />
    </svg>
  );
}
function SendIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z" />
    </svg>
  );
}

export function AidenProfileBanner({
  store,
  bean,
  brew,
  branch,
  tempChanged = false,
  className = "",
}) {
  const aidenProfile = brew?.aidenProfile || null;
  const baseName =
    aidenProfile?.title || brew?.profile?.label || brew?.profile?.id || "Balanced";

  const [nameOverride, setNameOverride] = useState(null);
  const [editing, setEditing] = useState(false);
  const [picking, setPicking] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null); // { ok, message, manual? }

  const profileName = nameOverride ?? baseName;

  const needsSend = branch !== "repeat" || tempChanged;
  const isNew = branch === "new_bag" || branch === "blend";
  const sendLabel = isNew ? "Save as a new Aiden profile" : "Update this Aiden profile";

  // Saved profiles the user can switch to (coffee_favorites). Prefer this bean's,
  // then any. In proxy mode we could also merge the brewer's on-device profiles.
  const savedProfiles = useMemo(() => {
    const favs = store?.favorites || [];
    const forBean = bean ? favs.filter((f) => f.bean_id === bean.id) : [];
    const rest = favs.filter((f) => !forBean.includes(f));
    return [...forBean, ...rest].slice(0, 8);
  }, [store?.favorites, bean]);

  const [aidenList, setAidenList] = useState(null);
  // Distinguish "the brewer genuinely has no profiles" from "we couldn't reach
  // it" — swallowing the failure into [] made a proxy outage look like an empty
  // brewer, with no retry.
  const [aidenErr, setAidenErr] = useState(null);
  const loadAiden = async () => {
    try { setAidenErr(null); setAidenList(await listAidenProfiles()); }
    catch (e) { setAidenErr(e?.message || "Couldn't reach the brewer"); setAidenList([]); }
  };
  const openPicker = async () => {
    setPicking((p) => !p);
    setResult(null);
    if (!picking && aidenProxyEnabled() && aidenList == null) await loadAiden();
  };

  const pickSaved = (fav) => {
    setNameOverride(fav.name || fav.title || profileName);
    setPicking(false);
  };
  const pickAiden = (p) => {
    setNameOverride(p.title || profileName);
    setPicking(false);
  };

  const send = async () => {
    if (sending || !needsSend) return;
    setSending(true);
    setResult(null);
    const profile = aidenProfile
      ? { ...aidenProfile, title: (profileName || baseName).slice(0, 30) }
      : null;
    try {
      // Persist a recallable copy for brand-new profiles (so "Change ›" can find it).
      if (isNew && bean && store?.saveFavorite && brew) {
        try {
          await store.saveFavorite({ name: (profileName || baseName).slice(0, 30), brew, beanId: bean.id });
        } catch (e) { console.warn("saveFavorite failed (non-fatal)", e?.message); }
      }
      if (!profile) {
        setResult({ ok: false, manual: true, message: "No machine profile to push — enter these on the Aiden by hand." });
        return;
      }
      const res = await pushProfileToAiden(profile);
      if (res.mode === "stub") {
        setResult({ ok: true, manual: true, message: res.message });
      } else {
        setResult({ ok: true, message: `Perfetto ☕ — ${isNew ? "saved" : "updated"} “${profileName}” on your Aiden.` });
      }
      setEditing(false);
    } catch (e) {
      // Graceful: show the values to enter manually instead of a dead error.
      setResult({ ok: false, manual: true, message: e?.message || "Couldn't reach the Aiden — enter these values by hand." });
    } finally {
      setSending(false);
    }
  };

  const manualText = result?.manual && aidenProfile ? formatProfileAsText({ ...aidenProfile, title: profileName }) : null;

  return (
    <div className={`rounded-[14px] bg-[rgba(63,74,99,0.08)] px-3.5 py-3 ${className}`}>
      <div className="flex items-center gap-2.5 mb-1.5">
        <span className="w-[26px] h-[26px] rounded-[8px] bg-pronto-navy grid place-items-center flex-shrink-0">
          <DialIcon />
        </span>
        <span className="flex-1 font-sf text-[10px] font-bold uppercase tracking-[0.06em] text-pronto-text-3">
          Aiden profile
        </span>
        <button
          onClick={openPicker}
          className="font-sf text-[12px] font-semibold text-pronto-navy flex-shrink-0"
          aria-expanded={picking}
        >
          Change ›
        </button>
      </div>

      {/* Name — editable when the recipe is new/edited */}
      {editing && needsSend ? (
        <input
          value={profileName}
          maxLength={30}
          autoFocus
          onChange={(e) => setNameOverride(e.target.value)}
          onBlur={() => setEditing(false)}
          aria-label="Aiden profile name"
          className="w-full font-round font-bold text-[18px] text-pronto-ink bg-white rounded-[9px] px-[9px] py-1.5 border-[1.5px] border-pronto-navy outline-none"
        />
      ) : (
        <div className="flex items-start gap-2">
          <div className="flex-1 font-round font-bold text-[20px] leading-[1.2] text-pronto-ink" style={{ textWrap: "pretty" }}>
            {profileName}
          </div>
          {needsSend && (
            <button
              onClick={() => setEditing(true)}
              aria-label="Rename Aiden profile"
              className="text-[14px] text-pronto-navy flex-shrink-0 pt-0.5"
            >
              ✎
            </button>
          )}
        </div>
      )}

      {/* Change ›: pick a saved profile */}
      {picking && (
        <div className="mt-2 rounded-[11px] bg-white/70 border border-[var(--p-sep-12)] overflow-hidden">
          {aidenErr ? (
            <button
              onClick={loadAiden}
              className="w-full text-left px-3 py-2.5 font-sf text-[12.5px] text-pronto-text-3"
            >
              Couldn&apos;t reach the brewer — tap to retry.
            </button>
          ) : savedProfiles.length === 0 && (!aidenList || aidenList.length === 0) ? (
            <div className="px-3 py-2.5 font-sf text-[12.5px] text-pronto-text-3">No saved profiles yet.</div>
          ) : (
            <>
              {savedProfiles.map((f) => (
                <button
                  key={f.id}
                  onClick={() => pickSaved(f)}
                  className="w-full text-left px-3 py-2 border-b-[0.5px] border-[var(--p-sep)] last:border-b-0"
                >
                  <span className="font-sf text-[13px] text-pronto-ink">{f.name || "Saved profile"}</span>
                  {f.grind_setting != null && (
                    <span className="font-sf text-[11.5px] text-pronto-text-3"> · Ode {f.grind_setting}</span>
                  )}
                </button>
              ))}
              {(aidenList || []).map((p) => (
                <button
                  key={p.id || p.title}
                  onClick={() => pickAiden(p)}
                  className="w-full text-left px-3 py-2 border-b-[0.5px] border-[var(--p-sep)] last:border-b-0"
                >
                  <span className="font-sf text-[13px] text-pronto-ink">{p.title}</span>
                  <span className="font-sf text-[11px] text-pronto-navy"> · on Aiden</span>
                </button>
              ))}
            </>
          )}
        </div>
      )}

      {/* Send / Save — only when the recipe is new or edited */}
      {needsSend && (
        <button
          onClick={send}
          disabled={sending}
          className="w-full h-10 rounded-[11px] bg-[linear-gradient(180deg,#55617E,#3F4A63)] text-white font-sf text-[13.5px] font-semibold mt-[11px] flex items-center justify-center gap-[7px] disabled:opacity-60"
        >
          <SendIcon />
          {sending ? "Sending…" : sendLabel}
        </button>
      )}

      {result && (
        <div className={`mt-2 font-sf text-[12px] leading-snug ${result.ok ? "text-[#1C7268]" : "text-pronto-warn"}`}>
          {result.message}
          {manualText && (
            <pre className="mt-1.5 whitespace-pre-wrap font-mono text-[11px] text-pronto-text-2 bg-white/60 rounded-[9px] px-2.5 py-2">{manualText}</pre>
          )}
        </div>
      )}
    </div>
  );
}
